// src/components/company/TrialBanner.tsx
import React from 'react';
import { AlertCircle, Clock } from 'lucide-react';

interface TrialBannerProps {
  trialEndsAt: string;
  subscriptionStatus: string;
  carrierCount?: number;
  carrierLimit?: number;
}

const TrialBanner: React.FC<TrialBannerProps> = ({ 
  trialEndsAt, 
  subscriptionStatus,
  carrierCount = 0,
  carrierLimit = 20
}) => {
  // Calculate days remaining
  const now = new Date();
  const endDate = new Date(trialEndsAt);
  const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // Don't show if active subscription
  if (subscriptionStatus === 'active') {
    return null;
  }

  // Trial expired
  if (subscriptionStatus === 'expired' || daysRemaining <= 0) {
    // Determine recommended plan based on carrier count
    const recommendedPlan = carrierCount <= 20 ? 'starter' : carrierCount <= 50 ? 'professional' : 'enterprise';
    
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Your trial has ended
              </p>
              <p className="text-sm text-red-700">
                {carrierCount > 0 && (
                  <span className="font-semibold">{carrierCount} carriers registered • </span>
                )}
                Choose a plan to restore full access to carrier management
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <a
              href={`/pricing?plan=${recommendedPlan}`}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700"
            >
              Choose Plan
            </a>
            <a
              href="/contact"
              className="px-4 py-2 bg-white text-red-600 text-sm font-medium rounded border border-red-600 hover:bg-red-50"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Trial active - show countdown
  const getBannerColor = () => {
    if (daysRemaining <= 2) return 'bg-orange-50 border-orange-500';
    if (daysRemaining <= 4) return 'bg-yellow-50 border-yellow-500';
    return 'bg-blue-50 border-blue-500';
  };

  const getTextColor = () => {
    if (daysRemaining <= 2) return 'text-orange-800';
    if (daysRemaining <= 4) return 'text-yellow-800';
    return 'text-blue-800';
  };

  const getIconColor = () => {
    if (daysRemaining <= 2) return 'text-orange-500';
    if (daysRemaining <= 4) return 'text-yellow-500';
    return 'text-blue-500';
  };

  // Determine recommended plan
  const recommendedPlan = carrierCount <= 20 ? 'starter' : carrierCount <= 50 ? 'professional' : 'enterprise';

  return (
    <div className={`${getBannerColor()} border-l-4 p-4 mb-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Clock className={`h-5 w-5 ${getIconColor()} mr-3`} />
          <div>
            <p className={`text-sm font-medium ${getTextColor()}`}>
              {daysRemaining === 1 
                ? '⚠️ Trial ends tomorrow!' 
                : `Trial Active: ${daysRemaining} days remaining`
              }
            </p>
            <p className={`text-sm ${getTextColor()}`}>
              {carrierCount > 0 && (
                <span className="font-semibold">{carrierCount} / {carrierLimit} carriers registered • </span>
              )}
              Upgrade now to keep full access after {new Date(trialEndsAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <a
          href={`/pricing?plan=${recommendedPlan}`}
          className={`px-4 py-2 ${
            daysRemaining <= 2 
              ? 'bg-orange-600 hover:bg-orange-700' 
              : daysRemaining <= 4
              ? 'bg-yellow-600 hover:bg-yellow-700'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white text-sm font-medium rounded`}
        >
          Upgrade Now
        </a>
      </div>
    </div>
  );
};

export default TrialBanner;
